import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLang, tx } from "@/context/LangContext";
import { PHOTO_GROUPS, T } from "@/data/content";
import { FadeUp, SectionLabel, Portal, DriftTitle } from "@/components/Reveal";
import EmptySlot from "@/components/EmptySlot";
import ShareMediaButton from "@/components/ShareMediaButton";
import { X, Image as ImageIcon, Plus } from "lucide-react";

const VISIBLE = 5;

// per-category radial color washes (same style as Ladurée) —
// subtle spots in each shoot's photo colors, cross-fading while scrolling
const GROUP_PALETTES = {
    // overdyed — beige, jaune, rouge, bleu
    overdyed: "radial-gradient(60% 50% at 75% 20%, rgba(232,216,180,0.32) 0%, transparent 70%), radial-gradient(55% 45% at 15% 55%, rgba(212,175,55,0.26) 0%, transparent 70%), radial-gradient(50% 40% at 80% 85%, rgba(176,48,48,0.28) 0%, transparent 70%), radial-gradient(45% 40% at 20% 95%, rgba(43,74,138,0.30) 0%, transparent 70%)",
    // kally kurls — blanc orangé
    kallykurls: "radial-gradient(60% 50% at 70% 25%, rgba(250,240,228,0.30) 0%, transparent 70%), radial-gradient(55% 45% at 20% 70%, rgba(232,150,60,0.30) 0%, transparent 70%), radial-gradient(45% 40% at 85% 90%, rgba(250,210,170,0.26) 0%, transparent 70%)",
    // theo — studio neutre
    theo: "radial-gradient(60% 50% at 70% 20%, rgba(160,160,170,0.25) 0%, transparent 70%), radial-gradient(55% 45% at 20% 70%, rgba(100,100,120,0.20) 0%, transparent 70%), radial-gradient(45% 40% at 85% 90%, rgba(140,140,150,0.18) 0%, transparent 70%)",
    // hollyg — concert, lumières chaudes
    hollyg: "radial-gradient(60% 50% at 70% 20%, rgba(255,180,100,0.28) 0%, transparent 70%), radial-gradient(55% 45% at 20% 70%, rgba(255,100,50,0.24) 0%, transparent 70%), radial-gradient(45% 40% at 85% 90%, rgba(255,200,150,0.22) 0%, transparent 70%)",
    // sijay-bpm — studio bleu/violet
    "sijay-bpm": "radial-gradient(60% 50% at 70% 20%, rgba(80,100,220,0.28) 0%, transparent 70%), radial-gradient(55% 45% at 20% 70%, rgba(140,80,200,0.24) 0%, transparent 70%), radial-gradient(45% 40% at 85% 90%, rgba(60,80,180,0.22) 0%, transparent 70%)",
    // esquimonoir — noir
    esquimonoir: "radial-gradient(60% 50% at 70% 20%, rgba(70,70,80,0.35) 0%, transparent 70%), radial-gradient(55% 45% at 15% 70%, rgba(0,0,0,0.55) 0%, transparent 75%), radial-gradient(45% 40% at 85% 90%, rgba(45,45,55,0.30) 0%, transparent 70%)",
    // pnasty — bleu violet
    pnasty: "radial-gradient(60% 50% at 70% 25%, rgba(70,90,220,0.30) 0%, transparent 70%), radial-gradient(55% 45% at 20% 70%, rgba(138,43,226,0.32) 0%, transparent 70%), radial-gradient(45% 40% at 85% 90%, rgba(40,50,160,0.28) 0%, transparent 70%)",
    // cars — vert forêt + rouge
    automotive: "radial-gradient(60% 50% at 70% 25%, rgba(35,77,44,0.32) 0%, transparent 70%), radial-gradient(55% 45% at 20% 70%, rgba(138,31,31,0.28) 0%, transparent 70%), radial-gradient(45% 40% at 85% 90%, rgba(20,40,26,0.30) 0%, transparent 70%)",
    // instants figés — tons chair chauds (629LA)
    "instants-figes": "radial-gradient(60% 50% at 70% 20%, rgba(190,130,95,0.30) 0%, transparent 70%), radial-gradient(55% 45% at 20% 70%, rgba(120,70,50,0.26) 0%, transparent 70%), radial-gradient(45% 40% at 85% 90%, rgba(220,170,130,0.22) 0%, transparent 70%)",
};

// shootings visibles par défaut — le reste derrière « VOIR PLUS »
const FEATURED_GROUPS = ["overdyed", "kallykurls", "theo", "automotive"];

const PATTERNS = {
    portrait: [
        { span: "md:col-span-4", ar: "aspect-[3/4]" },
        { span: "md:col-span-4 md:mt-16", ar: "aspect-[3/4]" },
        { span: "md:col-span-4 md:mt-32", ar: "aspect-[3/4]" },
        { span: "md:col-span-4", ar: "aspect-[4/5]" },
    ],
    landscape: [
        { span: "md:col-span-7", ar: "aspect-[16/10]" },
        { span: "md:col-span-5 md:mt-16", ar: "aspect-[4/3]" },
        { span: "md:col-span-5", ar: "aspect-[4/3]" },
        { span: "md:col-span-7 md:mt-16", ar: "aspect-[16/10]" },
    ],
    mixed: [
        { span: "md:col-span-7", ar: "aspect-[16/10]" },
        { span: "md:col-span-5 md:mt-16", ar: "aspect-[3/4]" },
        { span: "md:col-span-5", ar: "aspect-[3/4]" },
        { span: "md:col-span-7 md:mt-16", ar: "aspect-[16/10]" },
    ],
};

export default function Photography() {
    const { lang } = useLang();
    const [active, setActive] = useState(null);
    const [expanded, setExpanded] = useState({});
    const [palette, setPalette] = useState(PHOTO_GROUPS[0]?.id);
    const [showAllGroups, setShowAllGroups] = useState(false); // « voir plus » de shootings

    // active category drives the background gradient
    useEffect(() => {
        const obs = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) setPalette(e.target.dataset.gid);
                });
            },
            { rootMargin: "-35% 0px -35% 0px" }
        );
        document.querySelectorAll("[data-gid]").forEach((el) => obs.observe(el));
        return () => obs.disconnect();
    }, []);

    useEffect(() => {
        if (!active) return;
        window.__lenis?.stop?.();
        const onKey = (e) => e.key === "Escape" && setActive(null);
        window.addEventListener("keydown", onKey);
        return () => {
            window.__lenis?.start?.();
            window.removeEventListener("keydown", onKey);
        };
    }, [active]);

    const total = useMemo(
        () => PHOTO_GROUPS.reduce((n, g) => n + g.photos.length, 0),
        []
    );

    return (
        <section
            id="photography"
            data-testid="photography-section"
            className="relative overflow-x-clip bg-ink py-24 text-cream md:py-36"
        >
            {/* scroll-reactive gradient — radial washes in each category's photo colors */}
            <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
                {Object.entries(GROUP_PALETTES).map(([id, bg]) => (
                    <motion.div
                        key={id}
                        animate={{ opacity: palette === id ? 1 : 0 }}
                        transition={{ duration: 1.4, ease: "easeInOut" }}
                        className="absolute inset-0"
                        style={{ background: bg }}
                    />
                ))}
            </div>
            <div className="mx-auto max-w-[1600px] px-5 md:px-10">
                <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
                    <div>
                        <SectionLabel en={tx(lang, T.photo.label)} fr={tx(lang, T.photo.label)} />
                        <FadeUp delay={0.1}>
                            <DriftTitle dir={-1}>
                                <h2 className="mt-8 font-display text-[clamp(2.6rem,8vw,7rem)] font-extrabold uppercase leading-none tracking-[-0.02em]">
                                    {tx(lang, T.photo.title)}
                                    <span className="text-violet">.</span>
                                </h2>
                            </DriftTitle>
                        </FadeUp>
                    </div>
                    <FadeUp delay={0.2}>
                        <p className="font-mono text-[9px] tracking-[0.3em] text-cream/40 md:text-[10px]">
                            {tx(lang, T.photo.note)}
                        </p>
                    </FadeUp>
                </div>

                {PHOTO_GROUPS.filter((g) => showAllGroups || FEATURED_GROUPS.includes(g.id)).map((g, gi) => {
                    const pattern = PATTERNS[g.kind] || PATTERNS.portrait;
                    const isOpen = !!expanded[g.id];
                    const shown = isOpen ? g.photos : g.photos.slice(0, VISIBLE);
                    return (
                        <div
                            key={g.id}
                            data-testid={`photo-group-${g.id}`}
                            className={gi === 0 ? "mt-12 md:mt-20" : "mt-20 md:mt-28"}
                        >
                            <FadeUp>
                                <div className="mb-8 flex flex-wrap items-end justify-between gap-3 border-t border-white/10 pt-6 md:mb-10">
                                    <h3 className="font-display text-2xl font-extrabold uppercase tracking-tight md:text-4xl">
                                        {g.title}
                                        <span className="text-violet">.</span>
                                    </h3>
                                    <p className="font-mono text-[9px] tracking-[0.3em] text-cream/40 md:text-[10px]">
                                        {tx(lang, g.sub)} — {g.photos.length} PHOTOS
                                    </p>
                                </div>
                            </FadeUp>
                            <div className="grid grid-cols-2 gap-4 md:grid-cols-12 md:gap-8">
                                {shown.map((src, i) => {
                                    const p = pattern[i % pattern.length];
                                    const photo = {
                                        id: `${g.id}-${i + 1}`,
                                        img: src,
                                        title: `${g.title} N°${i + 1}`,
                                        sub: tx(lang, g.sub),
                                    };
                                    return (
                                        <motion.figure
                                            key={photo.id}
                                            initial={{ opacity: 0, y: 60, scale: 0.96, filter: "blur(8px)" }}
                                            whileInView={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
                                            viewport={{ once: true, amount: 0.12 }}
                                            transition={{
                                                duration: 1,
                                                delay: (i % 3) * 0.1,
                                                ease: [0.16, 1, 0.3, 1],
                                            }}
                                            className={p.span}
                                        >
                                            <button
                                                data-testid={`photo-${photo.id}`}
                                                data-cursor="VIEW"
                                                onClick={() => setActive(photo)}
                                                className="group relative block w-full overflow-hidden"
                                            >
                                                <div className={`w-full overflow-hidden ${p.ar}`}>
                                                    <img
                                                        src={photo.img}
                                                        alt={photo.title}
                                                        loading="lazy"
                                                        className="h-full w-full object-cover transition-all duration-[1100ms] ease-out group-hover:scale-[1.05]"
                                                    />
                                                </div>
                                            </button>
                                            <figcaption className="mt-3 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 font-mono text-[8px] tracking-[0.18em] text-cream/50 md:text-[10px] md:tracking-[0.25em]">
                                                <span>
                                                    N°{i + 1}
                                                    <span className="text-violet"> — {g.title}</span>
                                                </span>
                                                <span className="flex items-center gap-2">
                                                    <ShareMediaButton
                                                        kind="photo"
                                                        src={photo.img}
                                                        title={photo.title}
                                                        target="#photo"
                                                        testid={`share-photo-${photo.id}`}
                                                    />
                                                    <span>2026</span>
                                                </span>
                                            </figcaption>
                                        </motion.figure>
                                    );
                                })}
                            </div>
                            {g.photos.length > VISIBLE && (
                                <FadeUp delay={0.05}>
                                    <button
                                        data-testid={`photo-more-${g.id}`}
                                        onClick={() =>
                                            setExpanded((e) => {
                                                // expansion → on mémorise la position pour le bouton RETOUR
                                                if (!e[g.id]) window.__pushNavMark?.();
                                                return { ...e, [g.id]: !e[g.id] };
                                            })
                                        }
                                        className="group/more mt-8 flex items-center gap-3 font-mono text-[10px] tracking-[0.3em] text-cream/60 transition-colors hover:text-violet md:text-xs"
                                    >
                                        {isOpen
                                            ? tx(lang, T.photo.less)
                                            : `${tx(lang, T.photo.more)} — +${g.photos.length - VISIBLE}`}
                                        <Plus
                                            size={14}
                                            className={`transition-transform duration-500 ${isOpen ? "rotate-45" : "group-hover/more:rotate-90"}`}
                                        />
                                    </button>
                                </FadeUp>
                            )}
                        </div>
                    );
                })}

                {/* voir plus de shootings — réduit la page aux shootings phares */}
                {PHOTO_GROUPS.some((g) => !FEATURED_GROUPS.includes(g.id)) && (
                    <FadeUp delay={0.05} className="mt-16 md:mt-24">
                        <button
                            data-testid="photo-groups-toggle"
                            onClick={() =>
                                setShowAllGroups((v) => {
                                    // déplier plus de shootings → mémoriser la position pour RETOUR
                                    if (!v) window.__pushNavMark?.();
                                    return !v;
                                })
                            }
                            className="group/more flex items-center gap-3 border border-white/15 px-6 py-3.5 font-mono text-[10px] tracking-[0.3em] text-cream/70 transition-all hover:border-violet hover:text-violet md:text-xs"
                        >
                            {showAllGroups
                                ? tx(lang, T.photo.less)
                                : `${lang === "fr" ? "VOIR PLUS" : "SEE MORE"} — +${PHOTO_GROUPS.filter((g) => !FEATURED_GROUPS.includes(g.id)).length} SHOOTINGS`}
                            <Plus
                                size={14}
                                className={`transition-transform duration-500 ${showAllGroups ? "rotate-45" : "group-hover/more:rotate-90"}`}
                            />
                        </button>
                    </FadeUp>
                )}
            </div>

            <Portal><AnimatePresence>
                {active && (
                    <motion.div
                        data-testid="photo-lightbox"
                        className="fixed inset-0 z-[80] flex flex-col items-center justify-center bg-ink/96 p-5 backdrop-blur-md"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setActive(null)}
                    >
                        <button
                            data-testid="photo-lightbox-close"
                            onClick={() => setActive(null)}
                            className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full border border-white/20 text-cream transition-colors hover:border-violet hover:bg-violet"
                            aria-label="Close"
                        >
                            <X size={16} />
                        </button>
                        <motion.img
                            key={active.id}
                            src={active.img}
                            alt={active.title}
                            initial={{ scale: 0.94, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
                            className="max-h-[82vh] max-w-[94vw] object-contain"
                            onClick={(e) => e.stopPropagation()}
                        />
                        <p className="mt-5 font-mono text-[10px] tracking-[0.3em] text-cream/60">
                            {active.title} — {active.sub} — 2026
                        </p>
                    </motion.div>
                )}
            </AnimatePresence></Portal>
        </section>
    );
}
